// src/views/Operaciones/LiquidacionClientes/ListarLiquidacionClientes.jsx
import React, { useEffect, useState } from "react";
import { FaTrash, FaEdit } from "react-icons/fa";
import useEscClose from "../../useEscClose";
import apiRoutes from "../../../api/apiRoutes";
import { appFetchJson } from "../../../utils/http";
import ListHeader from "../../../components/listing/ListHeader";
import ListFooter from "../../../components/listing/ListFooter";
import FiltroView from "./FiltroLiquidacionSemanas";
import EliminarView from "./EliminarLiquidacionSemanas";
import CrearView from "./CrearLiquidacionSemanas";
import EditarView from "./EditarLiquidacionSemanas";

// ============================
// CONFIG
// ============================
const DEFAULT_PAGE_SIZE = 30;

// ============================
// COMPONENTE
// ============================
export default function ListarFacturacionClientesPorLiquidacion() {
  // ============================
  // ESTADO LISTA
  // ============================
  const [items, setItems] = useState([]);
  const [paginaActual, setPaginaActual] = useState(1); // UI 1-based
  const [porPagina, setPorPagina] = useState(DEFAULT_PAGE_SIZE);
  const [totalBackend, setTotalBackend] = useState(0);
  const [cargando, setCargando] = useState(false);

  // ============================
  // FILTROS
  // ============================
  const [filtros, setFiltros] = useState({});
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  useEscClose(() => setMostrarFiltros(false), mostrarFiltros);

  const limpiarSeleccion = () => {
    const empty = {};
    setFiltros(empty);
    setPaginaActual(1);
    fetchConsulta(1, porPagina, empty);
  };

  // ============================
  // MODALES
  // ============================

  // CREAR
  const [showCrear, setShowCrear] = useState(false);
  useEscClose(() => setShowCrear(false), showCrear);

  // ELIMINAR
  const [showEliminar, setShowEliminar] = useState(false);
  const [idEliminar, setIdEliminar] = useState(null);
  useEscClose(() => setShowEliminar(false), showEliminar);

  // EDITAR
  const [showEdit, setShowEdit] = useState(false);
  const [idEdit, setIdEdit] = useState(null);
  useEscClose(() => setShowEdit(false), showEdit);

  // ============================
  // FETCH REAL (ESTÁNDAR)
  // ============================
  const fetchConsulta = async (
    page = paginaActual,
    size = porPagina,
    filtrosAplicados = filtros
  ) => {
    setCargando(true);

    try {
      const data = await appFetchJson(
        apiRoutes.liquidacion.list({
          page: page - 1, // backend 0-based
          size,
          // ✅ FIX CLAVE
          descripcion: filtrosAplicados?.descripcion ?? null,
        })
      );

      setItems(data.items || []);
      setTotalBackend(data.totalElements || 0);
    } catch (e) {
      console.error("Error cargando liquidación", e);
      setItems([]);
      setTotalBackend(0);
    } finally {
      setCargando(false);
    }
  };

  // ============================
  // INIT
  // ============================
  useEffect(() => {
    fetchConsulta(1, porPagina, filtros);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================
  // PAGINACIÓN
  // ============================
  const totalPaginas = Math.max(1, Math.ceil(totalBackend / porPagina));

  const cambiarPagina = (num) => {
    const pagina = Math.min(Math.max(num, 1), totalPaginas);
    setPaginaActual(pagina);
    fetchConsulta(pagina, porPagina);
  };

  // ============================
  // RENDER
  // ============================
  return (
    <div className="page">
      <ListHeader
        title="Liquidacion (Semanas)"
        onFilterClick={() => setMostrarFiltros(true)}
        filters={filtros}
        onClearFilters={limpiarSeleccion}
        showAdd
        addLabel="Nuevo registro"
        onAddClick={() => setShowCrear(true)}
        pageSize={porPagina}
        onPageSizeChange={(size) => {
          setPorPagina(size);
          setPaginaActual(1);
          fetchConsulta(1, size, filtros);
        }}
      />

      {/* =========================
          FILTROS
         ========================= */}
      {mostrarFiltros && (
        <FiltroView
          initialFilters={filtros}
          onSubmit={(nuevosFiltros) => {
            setFiltros(nuevosFiltros);
            setPaginaActual(1);
            setMostrarFiltros(false);
            fetchConsulta(1, porPagina, nuevosFiltros);
          }}
          onClose={() => setMostrarFiltros(false)}
        />
      )}

      {/* =========================
          EDITAR
         ========================= */}
      {showEdit && (
        <EditarView
          id={idEdit}
          onClose={() => setShowEdit(false)}
          onSaved={() =>
            fetchConsulta(paginaActual - 1, porPagina, filtros)
          }
        />
      )}

      {/* =========================
          ELIMINAR
         ========================= */}
      {showEliminar && idEliminar && (
        <EliminarView
          id={idEliminar}
          onVolver={() => {
            setShowEliminar(false);
            setIdEliminar(null);
          }}
          onEliminado={() => {
            setShowEliminar(false);
            setIdEliminar(null);
            fetchConsulta(paginaActual, porPagina, filtros);
          }}
        />
      )}

      {/* =========================
          CREAR
         ========================= */}
      {showCrear && (
        <CrearView
          onClose={() => setShowCrear(false)}
          onSaved={() => {
            setShowCrear(false);
            fetchConsulta(1, porPagina, filtros);
          }}
        />
      )}

      {/* =========================
          TABLA
         ========================= */}
      <div className="overflow-x-auto mt-4">
        <table className="table-card table-sm">
          <thead className="table-head">
            <tr>
              <th className="cell text-left">Descripción</th>
              <th className="cell-center">Acción</th>
            </tr>
          </thead>

          <tbody>
            {cargando ? (
              <tr>
                <td colSpan={2} className="cell text-center">
                  Cargando…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={2} className="cell text-center">
                  Sin resultados
                </td>
              </tr>
            ) : (
              items.map((row, i) => (
                <tr key={row.id} className={i % 2 === 0 ? "row-alt" : ""}>
                  <td className="cell">{row.descripcion}</td>
                  <td className="cell-center space-x-2">
                    <button
                      className="icon-btn"
                      title="Editar"
                      onClick={() => {
                        setIdEdit(row.id);
                        setShowEdit(true);
                      }}
                    >
                      <FaEdit />
                    </button>

                    <button
                      className="icon-btn text-red-600"
                      title="Eliminar"
                      onClick={() => {
                        setIdEliminar(row.id);
                        setShowEliminar(true);
                      }}
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ListFooter
        page={paginaActual}
        pageSize={porPagina}
        total={totalBackend}
        onPageChange={cambiarPagina}
      />
    </div>
  );
}
